import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react'

import type { ProductCategory } from '@/lib/api/product-category-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateProductCategory,
    useDeleteProductCategory,
    usePatchProductCategory,
    useProductCategories,
} from '@/hooks/use-product-category-query'
import { m } from '@/lib/paraglide/messages'

// Danh mục sản phẩm (#M-16): phân nhóm sản phẩm theo cách đóng gói để mỗi sổ ghi
// chép của xưởng chỉ chọn được đúng nhóm sản phẩm của nó.
export const Route = createFileRoute(
    '/_app/_authed/_sidebar/products/categories',
)({
    component: ProductCategoriesPage,
})

interface FormState {
    code: string
    name: string
    sort_order: number
    is_active: boolean
}

const EMPTY_FORM: FormState = {
    code: '',
    name: '',
    sort_order: 0,
    is_active: true,
}

function ProductCategoriesPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('product_categories.write')

    const { data } = useProductCategories()
    const rows = [...(data?.data ?? [])].sort(
        (a, b) => a.sort_order - b.sort_order,
    )

    const [editing, setEditing] = useState<ProductCategory | null>(null)
    const [open, setOpen] = useState(false)

    const createMut = useCreateProductCategory()
    const patchMut = usePatchProductCategory()
    const deleteMut = useDeleteProductCategory()

    const openCreate = () => {
        setEditing(null)
        setOpen(true)
    }
    const openEdit = (row: ProductCategory) => {
        setEditing(row)
        setOpen(true)
    }
    const handleDelete = (row: ProductCategory) => {
        if (
            !window.confirm(
                m.product_category_delete_confirm({ name: row.name }),
            )
        ) {
            return
        }
        deleteMut.mutate(row.id)
    }

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.product_category_title()}
                subtitle={m.product_category_subtitle()}
                utilityActions={
                    canWrite && (
                        <Button size="sm" onClick={openCreate}>
                            <Plus className="size-4 mr-1" />
                            {m.product_category_create()}
                        </Button>
                    )
                }
            />

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-16 text-right">
                                {m.product_category_sort_order()}
                            </TableHead>
                            <TableHead className="w-36">
                                {m.product_category_code()}
                            </TableHead>
                            <TableHead className="min-w-40">
                                {m.product_category_name()}
                            </TableHead>
                            <TableHead className="w-28 text-right">
                                {m.product_category_products_count()}
                            </TableHead>
                            <TableHead className="w-28 text-center">
                                {m.product_category_is_active()}
                            </TableHead>
                            {canWrite && <TableHead className="w-24" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={canWrite ? 6 : 5}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.product_category_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell className="text-right tabular-nums">
                                        {row.sort_order}
                                    </TableCell>
                                    <TableCell className="font-mono">
                                        <span className="inline-flex items-center gap-1">
                                            {row.code}
                                            {row.is_system && (
                                                <Lock
                                                    className="size-3 text-muted-foreground"
                                                    aria-label={m.product_category_system()}
                                                />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {row.products_count}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Badge
                                            variant={
                                                row.is_active
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                        >
                                            {row.is_active ? '✓' : '—'}
                                        </Badge>
                                    </TableCell>
                                    {canWrite && (
                                        <TableCell className="text-right space-x-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={m.product_category_edit_aria(
                                                    { name: row.name },
                                                )}
                                                onClick={() => openEdit(row)}
                                            >
                                                <Pencil className="size-4" />
                                            </Button>
                                            <DeleteButton
                                                row={row}
                                                pending={deleteMut.isPending}
                                                onDelete={handleDelete}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {rows.length > 0 && (
                <p className="text-xs text-muted-foreground">
                    {m.product_category_total({ count: String(rows.length) })}
                </p>
            )}

            <p className="text-xs text-muted-foreground">
                {m.product_category_system_hint()}
            </p>

            <ProductCategoryDialog
                open={open}
                onOpenChange={setOpen}
                initial={editing}
                submitting={createMut.isPending || patchMut.isPending}
                onSubmit={async (values) => {
                    try {
                        if (editing) {
                            await patchMut.mutateAsync({
                                id: editing.id,
                                // Danh mục hệ thống không đổi được mã — không gửi
                                // trường code thay vì để server từ chối cả lần lưu.
                                data: editing.is_system
                                    ? {
                                          name: values.name,
                                          sort_order: values.sort_order,
                                          is_active: values.is_active,
                                      }
                                    : values,
                            })
                        } else {
                            await createMut.mutateAsync(values)
                        }
                        setOpen(false)
                    } catch {
                        // Toast hiện qua onError của mutation — giữ dialog để sửa tiếp.
                    }
                }}
            />
        </div>
    )
}

// Mã như TUI_12_5G — khớp server (productcategory codeRe, VARCHAR 50).
const CODE_MAX = 50
const CODE_RE = /^[A-Z0-9_]{1,50}$/

/**
 * Nút xoá kèm lý do hiện sẵn khi không xoá được: danh mục hệ thống, hoặc còn
 * sản phẩm. Người dùng phải biết vì sao TRƯỚC khi bấm.
 */
function DeleteButton({
    row,
    pending,
    onDelete,
}: {
    row: ProductCategory
    pending: boolean
    onDelete: (row: ProductCategory) => void
}) {
    const reason = deleteBlockReason(row)
    // Bọc span vì button disabled không bắn sự kiện chuột nên tooltip title
    // của chính nó sẽ không hiện.
    return (
        <span title={reason ?? undefined} className="inline-block">
            <Button
                variant="ghost"
                size="icon"
                aria-label={m.product_category_delete_aria({ name: row.name })}
                onClick={() => onDelete(row)}
                disabled={pending || reason !== null}
                className={reason ? 'pointer-events-none' : undefined}
            >
                <Trash2 className="size-4 text-red-500" />
            </Button>
        </span>
    )
}

function deleteBlockReason(row: ProductCategory): string | null {
    if (row.is_system) return m.product_category_delete_blocked_system()
    if (row.products_count > 0) {
        return m.product_category_delete_blocked_in_use({
            count: String(row.products_count),
        })
    }
    return null
}

/** Export để test được riêng hộp thoại mà không phải dựng cả router. */
export function ProductCategoryDialog({
    open,
    onOpenChange,
    initial,
    submitting,
    onSubmit,
}: {
    open: boolean
    onOpenChange: (v: boolean) => void
    initial: ProductCategory | null
    submitting: boolean
    onSubmit: (values: FormState) => Promise<void>
}) {
    // Đồng bộ form theo initial bằng key check thay vì useEffect — form ngắn,
    // đổi initial là dựng lại giá trị.
    //
    // Khoá gồm cả trạng thái đóng/mở: component này KHÔNG bị tháo khi đóng hộp
    // thoại, nên thiếu nó thì lần mở sau vẫn còn nguyên giá trị gõ dở và lỗi đỏ
    // của lần trước.
    const initialKey = `${open ? 'open' : 'closed'}:${initial?.id ?? 'new'}`
    const [key, setKey] = useState(initialKey)
    const [form, setForm] = useState<FormState>(() => initialToForm(initial))
    const justChanged = key !== initialKey
    if (justChanged) {
        setKey(initialKey)
        setForm(initialToForm(initial))
    }

    // Lỗi hiện DƯỚI từng ô, không chặn nút Lưu: bấm Lưu mà không có gì xảy ra và
    // cũng không nói vì sao là kiểu khó chịu nhất (quy tắc của repo).
    const [errors, setErrors] = useState<{ code?: string; name?: string }>({})
    if (justChanged && (errors.code || errors.name)) setErrors({})

    const codeLocked = initial?.is_system ?? false

    const validate = (values: FormState) => {
        const next: { code?: string; name?: string } = {}
        const code = values.code.trim()
        if (code.length === 0) next.code = m.product_category_code_required()
        // Chỉ kiểm định dạng khi mã đổi: mã cũ (trước luật) vẫn sửa tên được.
        else if (code !== (initial?.code ?? '') && !CODE_RE.test(code))
            next.code = m.product_category_code_format({
                max: String(CODE_MAX),
            })
        if (values.name.trim().length === 0)
            next.name = m.product_category_name_required()
        return next
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        {initial
                            ? m.product_category_edit()
                            : m.product_category_create()}
                    </DialogTitle>
                </DialogHeader>
                <form
                    className="space-y-3"
                    onSubmit={(e) => {
                        e.preventDefault()
                        const found = validate(form)
                        setErrors(found)
                        if (found.code || found.name) return
                        void onSubmit({
                            code: form.code.trim(),
                            name: form.name.trim(),
                            sort_order: form.sort_order,
                            is_active: form.is_active,
                        })
                    }}
                >
                    <div>
                        <Label>{m.product_category_code()} *</Label>
                        <Input
                            value={form.code}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    code: e.target.value.toUpperCase(),
                                })
                            }
                            placeholder={m.product_category_code_placeholder()}
                            className="font-mono"
                            disabled={codeLocked}
                            aria-invalid={errors.code ? true : undefined}
                        />
                        {errors.code && (
                            <p className="mt-1 text-xs text-destructive">
                                {errors.code}
                            </p>
                        )}
                        {codeLocked && (
                            <p className="mt-1 text-xs text-muted-foreground">
                                {m.product_category_system_hint()}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label>{m.product_category_name()} *</Label>
                        <Input
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder={m.product_category_name_placeholder()}
                            aria-invalid={errors.name ? true : undefined}
                        />
                        {errors.name && (
                            <p className="mt-1 text-xs text-destructive">
                                {errors.name}
                            </p>
                        )}
                    </div>
                    <div>
                        <Label>{m.product_category_sort_order()}</Label>
                        <Input
                            type="number"
                            value={form.sort_order}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    sort_order: Number(e.target.value) || 0,
                                })
                            }
                            className="w-24"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={form.is_active}
                            onCheckedChange={(v) =>
                                setForm({ ...form, is_active: v })
                            }
                        />
                        <Label>{m.product_category_is_active()}</Label>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button type="submit" disabled={submitting}>
                            {submitting ? '...' : m.common_save()}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

function initialToForm(initial: ProductCategory | null): FormState {
    if (!initial) return { ...EMPTY_FORM }
    return {
        code: initial.code,
        name: initial.name,
        sort_order: initial.sort_order,
        is_active: initial.is_active,
    }
}
